/**
 * Certificate Upload Component
 * File upload for medical certificates (PDF/images)
 *
 * @version 2025.1.0
 */
import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

interface CertificateUploadProps {
  onUpload: (file: File) => Promise<void>;
  onDelete?: () => Promise<void>;
  hasCertificate?: boolean;
  certificateFileName?: string;
  disabled?: boolean;
}

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const CertificateUpload: React.FC<CertificateUploadProps> = ({
  onUpload,
  onDelete,
  hasCertificate = false,
  certificateFileName,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Tipo de arquivo não permitido. Use PDF, JPEG, PNG ou GIF.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'Arquivo muito grande. Tamanho máximo: 10MB.';
    }
    return null;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setUploading(true);

    try {
      await onUpload(file);
    } catch (err: any) {
      setError(err.message || 'Erro ao fazer upload do atestado');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    if (!window.confirm('Tem certeza que deseja remover o atestado?')) {
      return;
    }

    setUploading(true);
    try {
      await onDelete();
    } catch (err: any) {
      setError(err.message || 'Erro ao remover atestado');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.gif"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {hasCertificate ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon color="success" />
              <AttachFileIcon color="action" />
              <Typography variant="body2">
                {certificateFileName || 'Atestado anexado'}
              </Typography>
            </Box>
            {onDelete && (
              <Tooltip title="Remover atestado">
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleDelete}
                  disabled={disabled || uploading}
                >
                  {uploading ? <CircularProgress size={20} /> : <DeleteIcon />}
                </IconButton>
              </Tooltip>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadIcon />}
              onClick={handleClick}
              disabled={disabled || uploading}
            >
              Substituir
            </Button>
          </>
        ) : (
          <Button
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
            onClick={handleClick}
            disabled={disabled || uploading}
          >
            {uploading ? 'Enviando...' : 'Anexar Atestado'}
          </Button>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Formatos aceitos: PDF, JPEG, PNG, GIF. Tamanho máximo: 10MB.
      </Typography>
    </Box>
  );
};

export default CertificateUpload;
